import EmptyState from "@/components/empty-state/EmptyState";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound";
import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { CandidateDisqualificationReasonEditModal } from "@/pages/settings/recruit/disqualification-reasons/widgets/CandidateDisqualificationReasonEditModal.tsx";
import {
  useDisqualificationDelete,
  useDisqualifications,
} from "@/queries/recruit/use-disqualifications.query.ts";
import analyticsService, { analyticEvents, analyticProperties } from "@/services/analytics-service";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiCandidateDisqualificationReason } from "@/types/recruit/disqualification-reason.types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export function CandidateDisqualificationReasons() {
  const { t } = useTranslation();
  useTitle(t("ats:disqualification_reasons"), t("common:settings"));
  const modalsService = useModals();
  const { data: disqualificationReasons, isLoading, isFetching } = useDisqualifications();
  const disqualificationReasonDelete = useDisqualificationDelete();
  const columns = useMemo<ColumnDef<ApiCandidateDisqualificationReason>[]>(
    () => [
      {
        header: t("common:title"),
        cell: (row) => <TableCell>{row.row.original.title}</TableCell>,
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
    ],
    [disqualificationReasons]
  );

  const handleEditOrCreate = useCallback(
    (disqualificationReason: ApiCandidateDisqualificationReason | undefined = undefined) => {
      const toEdit = disqualificationReason || {
        id: 0,
        title: "",
      };
      modalsService.openModal(CandidateDisqualificationReasonEditModal, {
        disqualificationReason: toEdit,
      });
    },
    []
  );

  const handleDeleteAction = useCallback(
    (disqualificationReason: ApiCandidateDisqualificationReason) => {
      modalsService.openModal(ConfirmationModal, {
        question: t("ats:disqualification_reason_delete_confirm"),
        text: t("ats:disqualification_reason_delete_confirm_description"),
        acceptTitle: t("common:delete"),
        onAccept: async function () {
          try {
            await disqualificationReasonDelete.mutateAsync(disqualificationReason.id);
            analyticsService.trackEvent(analyticEvents.disqualificationReasons.deleted, {
              [analyticProperties.id]: disqualificationReason.id,
            });
          } catch {
            return;
          }
        },
      });
    },
    []
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.disqualificationReasons.viewed);
  });

  const renderRowAction = useCallback(
    (row: Row<ApiCandidateDisqualificationReason>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleEditOrCreate(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            handleDeleteAction(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDeleteAction, handleEditOrCreate, t]
  );

  return (
    <>
      <Page>
        <Page.Header
          showBack={true}
          title={t("ats:disqualification_reasons")}
          navigateTo={"/settings/recruitment-directories"}
        >
          <Stack>
            <Button
              onClick={() => handleEditOrCreate()}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:create_new")}
            </Button>
          </Stack>
        </Page.Header>
        <Page.Content>
          <Table
            renderRowAction={renderRowAction}
            data={disqualificationReasons || []}
            columns={columns}
            isFetching={isFetching || disqualificationReasonDelete.isPending}
            showSkeleton={isLoading}
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={<EmptyState />}
          />
        </Page.Content>
      </Page>
    </>
  );
}
